import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { confirmPasswordReset } from '../remote';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const ResetPassword: React.FC = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const initialUid = query.get('uid') || '';
  const initialToken = query.get('token') || '';

  const [uid] = useState(initialUid);
  const [token  ] = useState(initialToken);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!uid || !token) {
      setError('Missing uid or token.');
      return;
    }
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const resp = await confirmPasswordReset({ uid, token, new_password: password });
      const msg = (resp as any)?.detail || 'Password has been reset successfully.';
      setSuccess(msg);
      // Clear any stale tokens after password change to avoid confusing login state
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refresh');
      } catch {}
      setTimeout(() => navigate('/login', { replace: true }), 1000);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
      <h2 className="mb-6 text-2xl font-bold text-black dark:text-white">Set New Password</h2>
      {error && <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-red-700">{error}</div>}
      {success && <div className="mb-4 rounded border border-green-300 bg-green-50 p-3 text-green-700">{success}</div>}

      <form onSubmit={onSubmit}>
        <div className="mb-3">
          <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">New Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-stroke bg-transparent p-3 text-black outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
          />
        </div>
        <div className="mb-4">
          <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">Confirm Password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-lg border border-stroke bg-transparent p-3 text-black outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg border border-primary bg-primary p-3 text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Saving…' : 'Reset Password'}
        </button>
        <div className="mt-4 text-center">
          <Link className="text-primary" to="/login">Back to login</Link>
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;
